import React from 'react';
import {
  ClipboardDocumentListIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import type { ConferenciasStats as IConferenciasStats } from '../../hooks/useConferencias';

interface ConferenciasStatsProps {
  stats: IConferenciasStats;
  isLoading?: boolean;
}

const StatCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'blue' | 'yellow' | 'green' | 'red' | 'purple' | 'indigo';
  isLoading?: boolean;
}> = ({ title, value, subtitle, icon, color, isLoading }) => {
  const colorClasses = {
    blue: 'bg-blue-500 text-blue-100',
    yellow: 'bg-amber-500 text-amber-100',
    green: 'bg-emerald-500 text-emerald-100',
    red: 'bg-rose-500 text-rose-100',
    purple: 'bg-purple-500 text-purple-100',
    indigo: 'bg-indigo-500 text-indigo-100',
  };

  const bgClasses = {
    blue: 'from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/10',
    yellow: 'from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/10',
    green: 'from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/10',
    red: 'from-rose-50 to-rose-100 dark:from-rose-900/20 dark:to-rose-800/10',
    purple: 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/10',
    indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/10',
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="h-4 w-24 rounded bg-zinc-200 animate-pulse dark:bg-zinc-700"></div>
            <div className="mt-2 h-8 w-16 rounded bg-zinc-300 animate-pulse dark:bg-zinc-600"></div>
            <div className="mt-1 h-3 w-20 rounded bg-zinc-200 animate-pulse dark:bg-zinc-700"></div>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-zinc-200 animate-pulse dark:bg-zinc-700"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`group rounded-xl border border-zinc-200 bg-gradient-to-br ${bgClasses[color]} p-6 shadow-sm hover:shadow-md transition-all dark:border-zinc-700`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h3 className="text-sm font-medium text-zinc-600 dark:text-zinc-400">{title}</h3>
          <div className="mt-2 flex items-baseline">
            <div className="text-3xl font-bold text-zinc-900 dark:text-white">{value}</div>
            {subtitle && (
              <div className="ml-2 text-sm text-zinc-500 dark:text-zinc-400">{subtitle}</div>
            )}
          </div>
        </div>
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${colorClasses[color]} group-hover:scale-110 transition-transform`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

const ConferenciasStats: React.FC<ConferenciasStatsProps> = ({ stats, isLoading }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-6">
      {/* Total de Conferências */}
      <StatCard
        title="Total de Conferências"
        value={stats.total}
        icon={<ClipboardDocumentListIcon className="h-6 w-6" />}
        color="blue"
        isLoading={isLoading}
      />

      {/* Em Andamento */}
      <StatCard
        title="Em Andamento"
        value={stats.emAndamento}
        icon={<ClockIcon className="h-6 w-6" />}
        color="yellow"
        isLoading={isLoading}
      />

      {/* Concluídas */}
      <StatCard
        title="Concluídas"
        value={stats.concluidas}
        icon={<CheckCircleIcon className="h-6 w-6" />}
        color="green"
        isLoading={isLoading}
      />

      {/* Canceladas */}
      <StatCard
        title="Canceladas"
        value={stats.canceladas}
        icon={<XCircleIcon className="h-6 w-6" />}
        color="red"
        isLoading={isLoading}
      />

      {/* Taxa de Conclusão */}
      <StatCard
        title="Taxa de Conclusão"
        value={`${stats.percentualConclusao}%`}
        icon={<ChartBarIcon className="h-6 w-6" />}
        color="purple"
        isLoading={isLoading}
      />

      {/* Produtividade */}
      <StatCard
        title="Atividade Recente"
        value={stats.total > 0 ? 'Ativa' : 'Baixa'}
        subtitle={stats.total > 0 ? `${stats.total} conferências` : 'Nenhuma conferência'}
        icon={<CalendarDaysIcon className="h-6 w-6" />}
        color="indigo"
        isLoading={isLoading}
      />
    </div>
  );
};

export default ConferenciasStats;